import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";
import { apiFetch } from "./lib/api";
import { getStoredUser, logout } from "./lib/auth";
import { AppLayout } from "./routes/layout";
import { LoginScreen } from "./routes/login";
import { AppShell } from "./routes/shell";
import { ToastProvider } from "./ui";

/**
 * Session gate. `siteIds` comes from the server rather than the stored user,
 * because a supervisor's site assignments can change while they are signed in
 * and the client must not cache an entitlement.
 */
function SessionGate(): ReactNode {
  const [user, setUser] = useState(() => getStoredUser());

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () =>
      apiFetch<{ role: string; siteIds: string[] | null }>("/auth/me"),
    enabled: user !== null,
    staleTime: 60_000,
  });

  if (user === null) {
    return (
      <AppLayout signedIn={false}>
        <LoginScreen onSignedIn={() => setUser(getStoredUser())} />
      </AppLayout>
    );
  }

  return (
    <AppLayout signedIn={true} isAdmin={user.role === "admin"}>
      <AppShell
        user={user}
        siteIds={me.data?.siteIds ?? null}
        onSignOut={() => void logout().then(() => setUser(null))}
      />
    </AppLayout>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <ToastProvider>
      <SessionGate />
      <Outlet />
    </ToastProvider>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => null,
});

const routeTree = rootRoute.addChildren([indexRoute]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
